import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom'; // 💳 Added useLocation
import { supabase, supabaseAnonKey } from '../supabaseClient';
import { LocalPrice } from '../components/local-price';
import { formatGBDate } from '../utils/date-format';
import { hasHorseCapacity } from '../utils/stable-capacity';

type LogRecord = {
    id: number;
    user_uuid: string;
    horse_uuid: string;
    horse_name: string;
    is_public: boolean;
    horse_image_url: string | null;
};

type PendingFarrierRequest = {
    id: number;
    horse_uuid: string;
    appointment_date: string;
    appointment_time: string | null;
    appointment_notes: string;
};

type SubscriptionMap = {
    [horseUuid: string]: boolean;
};

type SubscriptionDetails = {
    status: string;
    current_period_end: string | null;
};

type SubscriptionDetailsMap = {
    [horseUuid: string]: SubscriptionDetails;
};

type StripeCustomer = {
    id: string;
    horseNames: string[];
};

export default function Dashboard() {
    const [myLogs, setMyLogs] = useState<LogRecord[]>([]);
    const [horseName, setHorseName] = useState('');
    const [sessionUserId, setSessionUserId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [activeSubscriptions, setActiveSubscriptions] = useState<SubscriptionMap>({});
    const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetailsMap>({});
    const [pendingDeleteHorseUuid, setPendingDeleteHorseUuid] = useState<string | null>(null);
    const [deletingHorseUuid, setDeletingHorseUuid] = useState<string | null>(null);
    const [updatingPublicProfiles, setUpdatingPublicProfiles] = useState<SubscriptionMap>({});
    const [pendingFarrierRequests, setPendingFarrierRequests] = useState<PendingFarrierRequest[]>([]);
    const [farrierRequestsError, setFarrierRequestsError] = useState('');

    // Older subscriptions may be split across more than one Stripe customer.
    const [stripeCustomers, setStripeCustomers] = useState<StripeCustomer[]>([]);

    // 💳 Track if we are actively waiting for Stripe's webhook to finish updating
    const [isVerifyingPayment, setIsVerifyingPayment] = useState<boolean>(false);

    const location = useLocation();

    useEffect(() => {
        supabase.auth.getSession().then(({ data }) => {
            setSessionUserId(data.session?.user?.id ?? null);
        });
    }, []);

    // 💳 Extracted logic into a helper that returns whether an active subscription was found
    const fetchMyLogsAndSubscriptions = async (userId: string): Promise<boolean> => {
        // 1. Fetch horse logs
        const { data: logData, error: logError } = await supabase
            .from('equi_log_main')
            .select('id, user_uuid, horse_uuid, horse_name, is_public, horse_image_url')
            .eq('user_uuid', userId);

        if (logError) {
            console.error('Error fetching segmented records:', logError.message);
            return false;
        }

        const logs = ((logData as LogRecord[]) || []).map((log) => ({
            ...log,
            is_public: log.is_public ?? true,
        }));
        setMyLogs(logs);

        if (logs.length === 0) {
            setPendingFarrierRequests([]);
            setFarrierRequestsError('');
        } else {
            const { data: requestData, error: requestError } = await supabase
                .from('equi_farrier_appointment_requests')
                .select('id, horse_uuid, appointment_date, appointment_time, appointment_notes')
                .in('horse_uuid', logs.map((log) => log.horse_uuid))
                .eq('status', 'pending')
                .order('created_at', { ascending: true });

            if (requestError) {
                console.error('Error fetching farrier appointment requests:', requestError.message);
                setFarrierRequestsError('Could not load pending farrier appointment requests.');
                setPendingFarrierRequests([]);
            } else {
                setFarrierRequestsError('');
                setPendingFarrierRequests((requestData as PendingFarrierRequest[]) || []);
            }
        }

        // 2. Fetch subscription status along with Stripe Customer ID
        if (logs.length > 0) {
            const { data: subData, error: subError } = await supabase
                .from('equi_subscriptions')
                .select('horse_uuid, status, current_period_end, stripe_customer_id')
                .eq('user_uuid', userId);

            if (subError) {
                console.error('Error fetching subscriptions:', subError.message);
            } else if (subData && subData.length > 0) {
                const horseNamesByUuid = new Map(
                    logs.map((log) => [log.horse_uuid, log.horse_name])
                );
                const customersById = new Map<string, Set<string>>();

                subData.forEach((sub) => {
                    if (!sub.stripe_customer_id) return;

                    const horseNames =
                        customersById.get(sub.stripe_customer_id) ?? new Set<string>();
                    const matchingHorseName = horseNamesByUuid.get(sub.horse_uuid);
                    if (matchingHorseName) {
                        horseNames.add(matchingHorseName);
                    }
                    customersById.set(sub.stripe_customer_id, horseNames);
                });

                setStripeCustomers(
                    Array.from(customersById, ([id, horseNames]) => ({
                        id,
                        horseNames: Array.from(horseNames),
                    }))
                );

                const subMap: SubscriptionMap = {};
                const detailsMap: SubscriptionDetailsMap = {};
                subData.forEach((sub) => {
                    if (sub.horse_uuid) {
                        detailsMap[sub.horse_uuid] = {
                            status: sub.status,
                            current_period_end: sub.current_period_end,
                        };

                        if (sub.status === 'active') {
                            subMap[sub.horse_uuid] = true;
                        }
                    }
                });
                setActiveSubscriptions(subMap);
                setSubscriptionDetails(detailsMap);
                return Object.keys(subMap).length > 0;
            }
        }

        // If we got here, no active subscriptions were found
        setActiveSubscriptions({});
        setSubscriptionDetails({});
        setStripeCustomers([]);
        return false;
    };

    // Main data coordinator effect
    useEffect(() => {
        if (!sessionUserId) return;

        const queryParams = new URLSearchParams(location.search);
        const isPaymentSuccess = queryParams.get('payment') === 'success';

        const initializeDashboard = async () => {
            setLoading(true);
            const hasSub = await fetchMyLogsAndSubscriptions(sessionUserId);

            // 💳 If the URL says success, but our DB says otherwise, start polling!
            if (isPaymentSuccess && !hasSub) {
                setIsVerifyingPayment(true);

                let attempts = 0;
                const maxAttempts = 5; // Poll 5 times
                const intervalTime = 2000; // Every 2 seconds

                const intervalId = setInterval(async () => {
                    attempts += 1;
                    console.log(`Polling DB for webhook update... Attempt ${attempts}`);

                    const updatedHasSub = await fetchMyLogsAndSubscriptions(sessionUserId);

                    if (updatedHasSub || attempts >= maxAttempts) {
                        clearInterval(intervalId);
                        setIsVerifyingPayment(false);
                        setLoading(false);
                    }
                }, intervalTime);

                return () => clearInterval(intervalId);
            } else {
                setLoading(false);
            }
        };

        initializeDashboard();
    }, [sessionUserId, location.search]);

    const activeSubscriptionCount = Object.values(activeSubscriptions).filter(Boolean).length;
    const hasAnyActiveSubscription = activeSubscriptionCount > 0;
    const isHorseLimitReached = !hasHorseCapacity(myLogs.length, activeSubscriptionCount);
    // 🏷️ Get the ID of the first horse to link it directly to the purchase system
    const baseHorseId = myLogs[0]?.horse_uuid ?? '';
    const unsubscribedHorseId =
        myLogs.find((log) => !activeSubscriptions[log.horse_uuid])?.horse_uuid ?? baseHorseId;

    const handleManageBilling = async (stripeCustomerId: string) => {
        if (!stripeCustomerId) {
            alert("We couldn't find an active subscription profile link for your account yet.");
            return;
        }

        try {
            const isLocalhost =
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1';
            const {
                data: { session },
            } = await supabase.auth.getSession();

            if (!session?.access_token) {
                throw new Error('Your session has expired. Please sign in again.');
            }

            const functionUrl = isLocalhost
                ? 'http://127.0.0.1:54321/functions/v1/create-portal-session'
                : 'https://vjyvikuyuzkmyrtcuznc.supabase.co/functions/v1/create-portal-session';

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    apikey: supabaseAnonKey,
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({ customerId: stripeCustomerId }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok || data.error) {
                throw new Error(data.error || `Portal request failed (${response.status})`);
            }

            if (data.url) {
                window.location.href = data.url;
            } else {
                alert('Could not load billing gateway settings. Please try again later.');
            }
        } catch (err) {
            console.error('Portal error connection failure:', err);
            alert(
                err instanceof Error
                    ? err.message
                    : 'Error trying to connect securely to billing portal parameters.'
            );
        }
    };

    const addHorse = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!horseName || !sessionUserId) return;

        if (isHorseLimitReached) {
            alert('Stable limit reached. Please upgrade your subscription to add more horses.');
            return;
        }

        const { error = null } = await supabase.from('equi_log_main').insert([
            {
                horse_name: horseName,
                user_uuid: sessionUserId,
            },
        ]);

        if (error) {
            alert(error.message);
        } else {
            setHorseName('');
            setLoading(true);
            await fetchMyLogsAndSubscriptions(sessionUserId);
            setLoading(false);
        }
    };

    const handleDeleteHorseComplete = async (horseUuid: string, userId: string) => {
        setDeletingHorseUuid(horseUuid);

        const { data: activeSubscription, error: subscriptionError } = await supabase
            .from('equi_subscriptions')
            .select('id')
            .eq('horse_uuid', horseUuid)
            .eq('user_uuid', userId)
            .eq('status', 'active')
            .maybeSingle();

        if (subscriptionError) {
            setDeletingHorseUuid(null);
            alert(`Could not verify the subscription: ${subscriptionError.message}`);
            return;
        }

        if (activeSubscription) {
            setDeletingHorseUuid(null);
            alert('Cancel the active subscription before deleting this horse.');
            return;
        }

        const { error } = await supabase
            .from('equi_log_main')
            .delete()
            .eq('horse_uuid', horseUuid)
            .eq('user_uuid', userId);

        setDeletingHorseUuid(null);

        if (error) {
            alert(`Deletion failed: ${error.message}`);
        } else {
            window.location.reload();
        }
    };

    const handlePrivacyToggle = async (horseUuid: string, isPublic: boolean) => {
        if (!sessionUserId) return;

        setMyLogs((logs) =>
            logs.map((log) =>
                log.horse_uuid === horseUuid ? { ...log, is_public: isPublic } : log
            )
        );
        setUpdatingPublicProfiles((profiles) => ({ ...profiles, [horseUuid]: true }));

        const { error } = await supabase
            .from('equi_log_main')
            .update({ is_public: isPublic })
            .eq('horse_uuid', horseUuid)
            .eq('user_uuid', sessionUserId);

        setUpdatingPublicProfiles((profiles) => ({ ...profiles, [horseUuid]: false }));

        if (error) {
            setMyLogs((logs) =>
                logs.map((log) =>
                    log.horse_uuid === horseUuid ? { ...log, is_public: !isPublic } : log
                )
            );
            alert('Failed to update privacy setting: ' + error.message);
        }
    };

    return (
        <div className="page-wrapper">
            <div className="page-container">
                <section className="section-container purple-section-container no-print">
                    <h1 className="textbig nomargin">Your Stable</h1>

                    {/* 💳 Show a reassuring status indicator while polling */}
                    {isVerifyingPayment && (
                        <p
                            className="text-normal"
                            style={{
                                background: '#fef08a',
                                color: '#854d0e',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                marginTop: '10px',
                            }}
                        >
                            🔄 Confirming your payment details with Stripe... The page will unlock
                            automatically in a moment.
                        </p>
                    )}

                    {!hasAnyActiveSubscription && !isVerifyingPayment && (
                        <div>
                            <p className="text-normal marginbsixteen">
                                🔒 Some features are locked until you activate a subscription for at
                                least one horse.
                            </p>
                            <div>
                                {baseHorseId ? (
                                    <Link
                                        to={`/activate-tag?id=${baseHorseId}`}
                                        className="buttonOrange buttonMain"
                                    >
                                        Subscribe for <LocalPrice basePriceGbp={11} /> a year
                                    </Link>
                                ) : (
                                    <p className="text-normal">
                                        Please add your first horse below to continue.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </section>

                {farrierRequestsError && (
                    <section className="section-container white-section-container no-print marginbsixteen">
                        <p role="alert" className="text-normal">{farrierRequestsError}</p>
                    </section>
                )}

                {pendingFarrierRequests.length > 0 && (
                    <section className="section-container white-section-container no-print marginbsixteen">
                        <h2 className="textmedium marginbeight">
                            Pending Farrier Appointment Requests ({pendingFarrierRequests.length})
                        </h2>
                        <p className="text-normal marginbsixteen">
                            Review these requests before they are added to your calendar.
                        </p>
                        {pendingFarrierRequests.map((request) => {
                            const horse = myLogs.find((log) => log.horse_uuid === request.horse_uuid);
                            return (
                                <article
                                    key={request.id}
                                    className="marginbsixteen"
                                    style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '16px' }}
                                >
                                    <p className="text-normal marginbeight">
                                        <strong>{horse?.horse_name ?? 'Your horse'}</strong>
                                        {' — Farrier visit '}
                                        {formatGBDate(request.appointment_date)}
                                        {request.appointment_time && ` at ${request.appointment_time.slice(0, 5)}`}
                                    </p>
                                    {request.appointment_notes && (
                                        <p className="text-normal marginbeight">{request.appointment_notes}</p>
                                    )}
                                    <Link
                                        to={`/farrier-appointment-requests/${request.horse_uuid}`}
                                        className="buttonMain buttonPurple"
                                    >
                                        Review request
                                    </Link>
                                </article>
                            );
                        })}
                    </section>
                )}

                {loading && !isVerifyingPayment ? (
                    <section className="section-container white-section-container no-print marginbsixteen">
                        <p className="text-normal">Loading your stable profile...</p>
                    </section>
                ) : myLogs.length === 0 ? (
                    <section className="section-container white-section-container no-print marginbsixteen">
                        <p
                            className="text-normal"
                            style={{ color: '#64748b', marginBottom: '20px' }}
                        >
                            No horses registered under this account yet.
                        </p>

                        <section className="section-container white-section-container no-print">
                            <h2 className="textmedium marginbeight">Add your First Horse</h2>
                            <form onSubmit={addHorse}>
                                <input
                                    className="inputText marginbsixteen"
                                    value={horseName}
                                    onChange={(e) => setHorseName(e.target.value)}
                                    placeholder="Enter horse name"
                                    required
                                />
                                <br />
                                <button type="submit" className="buttonPurple buttonMain">
                                    Add new horse to Stable
                                </button>
                            </form>
                        </section>
                    </section>
                ) : (
                    <>
                        <>
                            {/* 2. HORSE MANAGEMENT ROSTER */}
                            {myLogs.map((log) => {
                                const isSubbed = !!activeSubscriptions[log.horse_uuid];
                                const protection = subscriptionDetails[log.horse_uuid];
                                return (
                                    <section
                                        className="section-container white-section-container no-print marginbsixteen"
                                        key={`manage-${log.id}`}
                                    >
                                        <div className="dashboard-horse-split">
                                            <div className="dashboard-horse-split-column">
                                                <div className="marginbsixteen">
                                                    <div className="dashboard-horse-heading">
                                                        {log.horse_image_url && (
                                                            <img
                                                                src={log.horse_image_url}
                                                                alt=""
                                                                className="dashboard-horse-avatar"
                                                            />
                                                        )}
                                                        <h2
                                                            className="textbig"
                                                            style={{ margin: 0 }}
                                                        >
                                                            Registered horse:
                                                            <br />
                                                            <strong>{log.horse_name}</strong>
                                                        </h2>
                                                    </div>
                                                </div>
                                                {isSubbed && (
                                                    <div className="big-switch white-section-container section-container marginbtwenfour">
                                                        <div>
                                                            <div className="text-normal">
                                                                <strong>
                                                                    🌐 Switch for Public Profile
                                                                </strong>
                                                            </div>
                                                            <div
                                                                className={`privacy-toggle-meta ${log.is_public ? '' : 'redclass'}`}
                                                            >
                                                                {updatingPublicProfiles[
                                                                    log.horse_uuid
                                                                ]
                                                                    ? 'Saving changes...'
                                                                    : log.is_public
                                                                      ? 'Profile is Live and can be seen publicly'
                                                                      : 'Profile is Hidden from public view'}
                                                            </div>
                                                        </div>
                                                        <label
                                                            className="switch"
                                                            htmlFor={`is_public_${log.horse_uuid}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                id={`is_public_${log.horse_uuid}`}
                                                                checked={log.is_public}
                                                                onChange={(event) =>
                                                                    handlePrivacyToggle(
                                                                        log.horse_uuid,
                                                                        event.target.checked
                                                                    )
                                                                }
                                                                disabled={
                                                                    updatingPublicProfiles[
                                                                        log.horse_uuid
                                                                    ]
                                                                }
                                                            />
                                                            <span className="slider round"></span>
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="dashboard-horse-split-column marginbsixteen">
                                                <div className="section-container lightorange-section-container">
                                                    <h2 className="textmedium marginbeight">
                                                        🛡️ Live Tag Subscription
                                                    </h2>
                                                    <div
                                                        className="text-normal marginbeight datarow"
                                                        style={{ maxWidth: '400px' }}
                                                    >
                                                        <span>Tag Link Status:</span>{' '}
                                                        <strong
                                                            style={{
                                                                color:
                                                                    protection?.status === 'active'
                                                                        ? '#16a34a'
                                                                        : '#dc2626',
                                                            }}
                                                        >
                                                            {protection?.status?.toUpperCase() ||
                                                                'INACTIVE'}
                                                        </strong>
                                                    </div>
                                                    <div
                                                        className="text-normal datarow"
                                                        style={{ maxWidth: '400px' }}
                                                    >
                                                        <span>Renewal Date:</span>{' '}
                                                        <strong>
                                                            {protection?.current_period_end
                                                                ? formatGBDate(
                                                                      protection.current_period_end
                                                                  )
                                                                : 'Not Documented'}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <ul className="olnone">
                                            <li className="marginbsixteen">
                                                <Link
                                                    to={`/owner-horse-details/${log.horse_uuid}`}
                                                    className="buttonMain buttonPurple"
                                                >
                                                    👁️ &nbsp;View full horse details
                                                </Link>
                                            </li>
                                            <li className="marginbsixteen">
                                                <Link
                                                    to={
                                                        isSubbed
                                                            ? `/horse-details/${log.horse_uuid}`
                                                            : `/activate-tag?id=${log.horse_uuid}`
                                                    }
                                                    className="buttonMain buttonPurple"
                                                    target={isSubbed ? '_self' : '_blank'}
                                                >
                                                    {!isSubbed ? '🔒 ' : '🐴 '} &nbsp;See your
                                                    public profile
                                                </Link>
                                            </li>
                                        </ul>
                                        <ul className="olnone">
                                            <li className="marginbtwenfour">
                                                <Link
                                                    to={`/edit-horse/${log.horse_uuid}`}
                                                    className="buttonLink marginbsixteen"
                                                >
                                                    Edit horse details&nbsp;&nbsp;✏️
                                                </Link>
                                            </li>
                                            <li className="marginbtwenfour">
                                                <Link
                                                    to={
                                                        isSubbed
                                                            ? `/privacy/${log.horse_uuid}`
                                                            : `/activate-tag?id=${log.horse_uuid}`
                                                    }
                                                    className="buttonLink marginbsixteen"
                                                >
                                                    Edit what the public see&nbsp;&nbsp;
                                                    {!isSubbed ? '🔒 ' : '✏️️ '}
                                                </Link>
                                            </li>
                                        </ul>
                                    </section>
                                );
                            })}

                            {/* 1. PRINTING UTILITIES BLOCK */}
                            <section className="section-container white-section-container no-print marginbsixteen">
                                <div className="marginbsixteen">
                                    <h2 className="textmedium" style={{ margin: 0 }}>
                                        🖨️ Print your tags
                                    </h2>
                                </div>
                                {myLogs.map((log) => {
                                    const isSubbed = !!activeSubscriptions[log.horse_uuid];
                                    return (
                                        <ul key={`print-${log.id}`} className="olnone">
                                            <li className="marginbsixteen">
                                                <div className="marginbtwenfour">
                                                    <a
                                                        href={
                                                            isSubbed
                                                                ? `/print-stable-tag?id=${log.horse_uuid}&name=${encodeURIComponent(log.horse_name)}`
                                                                : '/print-stable-tag-dummy'
                                                        }
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                        className="buttonLink"
                                                    >
                                                        Print <b>{log.horse_name}</b> Stable Tag
                                                        {!isSubbed && ' preview'}
                                                        {!isSubbed && `🔒`}
                                                    </a>
                                                </div>
                                            </li>
                                            <li>
                                                <div className="marginbtwenfour">
                                                    <a
                                                        href={
                                                            isSubbed
                                                                ? `/print-horsebox-poster?id=${log.horse_uuid}&name=${encodeURIComponent(log.horse_name)}`
                                                                : '/print-horsebox-poster-dummy'
                                                        }
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                        className="buttonLink"
                                                    >
                                                        Print <b>{log.horse_name}</b> Poster
                                                        {!isSubbed && ' preview'}
                                                        {!isSubbed && '🔒'}
                                                    </a>
                                                </div>
                                            </li>
                                        </ul>
                                    );
                                })}
                            </section>
                        </>

                        {/* 3. USER ASSETS PANEL */}
                        <section className="section-container white-section-container no-print marginbsixteen">
                            <h2 className="textmedium marginbtwenfour">
                                ⚙️ &nbsp;&nbsp;Global Tools & Assets
                            </h2>
                            <ul className="olnone">
                                <li className="marginbtwenfour">
                                    <Link
                                        to={
                                            hasAnyActiveSubscription
                                                ? `/calendar`
                                                : `/activate-tag?id=${baseHorseId}`
                                        }
                                        className="buttonLink"
                                    >
                                        View your stable calendar
                                        {!hasAnyActiveSubscription && '\u00A0\u00A0🔒'}
                                    </Link>
                                </li>
                                <li className="marginbtwenfour">
                                    <Link
                                        to={
                                            hasAnyActiveSubscription
                                                ? `/horsebox-view`
                                                : `/activate-tag?id=${baseHorseId}`
                                        }
                                        className="buttonLink"
                                    >
                                        View your horsebox details
                                        {!hasAnyActiveSubscription && '\u00A0\u00A0🔒'}
                                    </Link>
                                </li>
                                {stripeCustomers.map((stripeCustomer) => (
                                    <li className="marginbtwenfour" key={stripeCustomer.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleManageBilling(stripeCustomer.id)}
                                            className="buttonLink buttonWhite"
                                        >
                                            Manage Billing & Cancellations
                                            {stripeCustomer.horseNames.length > 0
                                                ? ` (${stripeCustomer.horseNames.join(', ')})`
                                                : ''}
                                        </button>
                                    </li>
                                ))}
                                <li className="marginbtwenfour">
                                    <Link to="/add-bookmark" className="buttonLink">
                                        Add a bookmark to your phones home screen.
                                    </Link>
                                </li>
                            </ul>
                        </section>

                        {/* ➕ CONDITIONAL ADD NEW HORSE UTILITY */}
                        {isHorseLimitReached ? (
                            <section className="section-container white-section-container no-print">
                                <h2 className="textmedium marginbeight">🐴 Stable Limit Reached</h2>
                                <p
                                    className="text-normal"
                                    style={{ color: '#64748b', marginBottom: '12px' }}
                                >
                                    Your stable includes one horse without a subscription. Subscribe
                                    to that horse to unlock another horse profile.
                                </p>
                                <a
                                    href={`/activate-tag?id=${unsubscribedHorseId}`}
                                    className="buttonOrange buttonMain"
                                    style={{
                                        textDecoration: 'none',
                                        display: 'inline-block',
                                        textAlign: 'center',
                                    }}
                                >
                                    Subscribe for <LocalPrice basePriceGbp={11} /> a year
                                </a>
                            </section>
                        ) : (
                            <section className="section-container white-section-container no-print">
                                <h2 className="textmedium marginbeight">Add a New Horse</h2>
                                <form onSubmit={addHorse}>
                                    <input
                                        className="inputText marginbsixteen"
                                        value={horseName}
                                        onChange={(e) => setHorseName(e.target.value)}
                                        placeholder="Enter horse name"
                                        required
                                    />
                                    <br />
                                    <button type="submit" className="buttonPurple buttonMain">
                                        Add new horse to Stable
                                    </button>
                                </form>
                            </section>
                        )}
                        {myLogs.length === 0 ? null : (
                            <section className="section-container white-section-container no-print">
                                <h2 className="textmedium marginbeight">
                                    Delete your horse and all associated records
                                </h2>
                                <p className="text-normal marginbsixteen">
                                    Be careful, once you delete the records they are not
                                    recoverable!
                                </p>
                                {myLogs.map((log) => {
                                    const isConfirmingDelete =
                                        pendingDeleteHorseUuid === log.horse_uuid;
                                    const isDeleting = deletingHorseUuid === log.horse_uuid;
                                    const hasActiveSubscription =
                                        activeSubscriptions[log.horse_uuid] === true;

                                    return (
                                        <div
                                            key={`manage-${log.id}`}
                                            className="dashboard-delete-horse"
                                        >
                                            <div className="dashboard-horse-heading">
                                                {log.horse_image_url && (
                                                    <img
                                                        src={log.horse_image_url}
                                                        alt=""
                                                        className="dashboard-horse-avatar"
                                                    />
                                                )}
                                                <h4 className="dashboard-horse-name">
                                                    {log.horse_name}
                                                </h4>
                                            </div>
                                            {hasActiveSubscription && (
                                                <p
                                                    className="text-normal marginbsixteen"
                                                    role="status"
                                                >
                                                    You can delete your horse once your subscription
                                                    has completed.
                                                </p>
                                            )}

                                            {isConfirmingDelete ? (
                                                <div
                                                    className="dashboard-delete-confirmation"
                                                    role="alert"
                                                >
                                                    <p className="text-normal">
                                                        <strong>Are you sure?</strong> This will
                                                        permanently wipe {log.horse_name}, its
                                                        entire calendar, and all its associated
                                                        records. These cannot be recovered.
                                                    </p>
                                                    <div className="dashboard-delete-actions">
                                                        <button
                                                            type="button"
                                                            className="buttonSmall"
                                                            onClick={() =>
                                                                setPendingDeleteHorseUuid(null)
                                                            }
                                                            disabled={isDeleting}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="buttonSmall dashboard-delete-confirm-button"
                                                            onClick={() =>
                                                                handleDeleteHorseComplete(
                                                                    log.horse_uuid,
                                                                    sessionUserId
                                                                )
                                                            }
                                                            disabled={isDeleting}
                                                        >
                                                            {isDeleting
                                                                ? 'Deleting...'
                                                                : `Permanently delete ${log.horse_name}`}
                                                        </button>
                                                    </div>

                                                    <hr />
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="buttonSmall marginbsixteen buttonfullwidth"
                                                    style={{
                                                        backgroundColor: '#ff0000',
                                                        color: '#ffffff',
                                                        fontWeight: 'bold',
                                                    }}
                                                    onClick={() =>
                                                        setPendingDeleteHorseUuid(log.horse_uuid)
                                                    }
                                                    disabled={hasActiveSubscription}
                                                >
                                                    {hasActiveSubscription
                                                        ? `Cannot delete while subscription is active, check your end date.`
                                                        : `Delete ${log.horse_name}`}
                                                </button>
                                            )}
                                            <hr />
                                        </div>
                                    );
                                })}
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
